import { v4 as uuidv4 } from 'uuid'
import { z } from 'zod'
import { parseDateTime } from '../utils/date-to-string'
import {
  searchQueryApiSchema,
  searchQueryWithFiltersSchema,
} from './search-query'
import { searchQueryIntersectionDataSchema } from './search-query-intersection'
import { tagSchema } from './tags'

export const baseSearchApiSchema = z.object({
  id: z.string(),
  number_of_searches: z.number(),
  title: z.string(),
  updated_at: z.string(),
  search_queries: z
    .array(searchQueryWithFiltersSchema)
    .nullable()
    .default(null),
  search_query_intersections: z
    .array(searchQueryIntersectionDataSchema)
    .nullable()
    .default(null),
  active_bulk_question: z.string().nullable().default(null),
})

export const searchApiSchema = baseSearchApiSchema
  .transform((data) => {
    const {
      number_of_searches,
      updated_at,
      search_queries,
      search_query_intersections,
      active_bulk_question,
      ...rest
    } = data
    const projectStatus = number_of_searches > 0 ? 'with_query' : 'new'
    const progressName =
      projectStatus === 'with_query' ? 'Results' : 'Description'
    return {
      ...rest,
      numberOfSearches: number_of_searches,
      updatedAt: parseDateTime(updated_at),
      progressName,
      searchQueries: search_queries,
      searchQueryIntersections: search_query_intersections,
      activeBulkQuestion: active_bulk_question,
      instanceId: uuidv4(),
      projectStatus,
    }
  })
  .describe('AI Search Type')

export type AISearchTypeAPI = z.infer<typeof searchApiSchema>

export const taggedSearchSchema = baseSearchApiSchema
  .pick({
    id: true,
    title: true,
    updated_at: true,
    number_of_searches: true,
  })
  .extend({
    created_at: z.string(),
    tags: tagSchema.array(),
    search_queries: searchQueryApiSchema
      .pick({
        id: true,
        query: true,
        count: true,
        type: true,
      })
      .array(),
  })
  .transform((data) => {
    const {
      search_queries,
      number_of_searches,
      created_at,
      updated_at,
      ...rest
    } = data

    const projectStatus = number_of_searches > 0 ? 'with_query' : 'new'
    const progressName =
      projectStatus === 'with_query' ? 'Results' : 'Description'

    return {
      ...rest,
      searchQueries: search_queries,
      numberOfSearches: number_of_searches,
      createdAt: created_at,
      updatedAt: updated_at,
      projectStatus,
      progressName,
    }
  })

export type TaggedSearchType = z.infer<typeof taggedSearchSchema>
